/*global _ */
import {getProductById} from '../../../lib/store/product'

const PRODUCT_TYPE = 'reservation'
import {Product} from '../Product'
import {getNow, now, parseDateTime, getDate, getDateTime, formatDate} from '../../../lib/dates'

import formatters from '../../../lib/formatters'
const {nil, nils, notNils, notNil, empty, notEmpty, toBoolean, groupByKeyPrefix, toNumber, firstKey, first} = formatters

export class Reservation extends Product {
  constructor(data, vm) {
    super(data, vm)
    this.comboProducts = _.mapValues(data.combos || {}, p => new Reservation(p, vm))
  }

  get combos() {
    let combos = this.comboProducts
    if (empty(combos) && this.isComboMaster) {
      this.fillComboProducts()
      combos = this.comboProducts
    }
    return combos
  }

  get isCombo() {
    return !!this.details.combo_id
  }

  get isComboMaster() {
    return this.isCombo && toBoolean(this.details.combo_master)
  }

  get isComboSlave() {
    return this.isCombo && !this.isComboMaster
  }

  get hasCombo() {
    return notEmpty(this.combos)
  }

  get durationRaw() {
    return 1 * this.data.duration
  }

  get duration() {
    return this.getUnitiedDuration()
  }

  get durationUnit() {
    return this.$$s('duration.unit', 'hour')
  }

  get durationFactor() {
    const factors = {
      hour: 1 / 60,
      day: 1 / (60 * 24),
    }
    const factor = this.$$s('duration.factor', 'hour')
    return factors[factor] || 1
  }

  get additionalDurations() {
    let originalDurationRaw = this.durationRaw
    let originalDuration = this.duration
    let originalMax = this.maxSlots

    let additional = this.additionalPricing

    if (empty(additional) && this.maxSlots > 1) {
      additional = {durationRaw: originalDurationRaw, duration: originalDuration, max: originalMax}
      originalDurationRaw = 0
      originalDuration = 0
      originalMax = 0
    }

    if (notEmpty(additional)) {
      const {durationRaw, duration, max} = additional
      const startRange = durationRaw + originalDurationRaw
      const range = _.range(startRange, startRange + max * durationRaw, durationRaw)
      return range
    }
    return []
  }

  get durations() {
    let durations = [
      this.durationRaw,
      ...this.additionalDurations
    ]

    _.forOwn(this.combos, c => {
      const comboDurations = c.durations

      durations = [...durations, ...comboDurations]
    })

    return _.uniq(durations).sort((a, b) => a - b)
  }

  get durationOptions() {

    const durations = this.durations

    const values = (_.uniq(durations)).sort((a, b) => a - b)
    const options = _.reduce(values, (o, rawValue) => {
      o[rawValue] = this.getDurationOption(rawValue)
      return o
    }, {})

    return options
  }

  get additionalDurationOptions() {

    let rawDurations = [this.durationRaw]
    _.forOwn(this.combos, c => {
      rawDurations.push(c.durationRaw)
    })
    const rawDuration = Math.max(...rawDurations)

    let durations = this.additionalDurations
    _.forOwn(this.combos, c => {
      durations = durations.concat(c.additionalDurations)
    })
    const values = (_.uniq(durations)).sort((a, b) => a - b)
    const options = _.reduce(values, (o, rawValue) => {
      if (rawValue > rawDuration) {
        o[rawValue] = this.getDurationOption(rawValue - rawDuration)
      }
      return o
    }, {})
    return options
  }

  get slotsByDuration() {
    let max = this.maxSlots
    let slots = this.settings.slotsByDuration
    _.forOwn(this.combos, c => {
      slots = {...slots, ...c.settings.slotsByDuration}
      max = Math.max(max, c.maxSlots)
    })
    const extra = this.extraSlotsByDuration
    _.forOwn(extra, (s, k) => {
      slots[k] = max
    })
    return slots
  }
  get extraSlotsByDuration() {
    let fixedSlots = this.settings.slotsByDuration
    let slots = this.settings.extraSlotsByDuration
    _.forOwn(this.combos, c => {
      fixedSlots = {...fixedSlots, ...c.settings.slotsByDuration}
      slots = {...slots, ...c.settings.extraSlotsByDuration}
    })
    return _.omit(slots, _.keys(fixedSlots))
  }

  /*
  "upgrades": [
    {
      "name": "cake",
      "title": "Cake",
      "fixed_price": "1",
      "price": "25",
      "max_count": "999"
    }
  ],
   */
  get upgrades() {
    const source = this.data.upgrades
    const upgrades = _.reduce(source, (c, i) => {
      const isFixedPrice = !!(1 * i.fixed_price)
      c[i.name] = {
        type: 'upgrade',
        name: i.name,
        title: i.title,
        price: i.price * 1,
        perPerson: !isFixedPrice,
        perReservation: isFixedPrice,
        max: i.max_count * 1,
      }
      return c
    }, {})
    return upgrades
  }

  /*
    "addons": [
      {
        "addon_product_id": "100"
        "max_count": "999"
      }
    ],
  */
  get addons() {
    const source = this.data.addons
    if (_.isNil(source)) {
      return null
    }
    return _.reduce(source, (c, i) => {
      const productId = i.addon_product_id
      const max = i.max_count
      c[productId] = {
        id: productId,
        product: getProductById(productId),
        max: max * 1,
      }
      return c
    }, {})
  }

  get initAddonsQuantity() {
    const source = this.data.addons || {}
    return _.reduce(source, (c, i) => {
      c[i.addon_product_id] = 0
      return c
    }, {})
  }

  get selectedAddons() {
    const inputs = this.$$vm().fields || {}
    const addonDef = this.addons || {}
    const addons = inputs.addons || {}
    return _.reduce(addons, (acc, quantity, id) => {
      const product = addonDef[id]?.product
      if (product && quantity > 0) {
        if (_.isNil(acc)) {
          acc = []
        }
        acc.push(product.title + ' (' + quantity + ')')
      }
      return acc
    }, null)
  }

  /**
   *
   */
  get addonNamesWithQuantity() {
    const inputs = this.$$vm().fields || {}
    const addonDef = this.addons || {}
    const addons = inputs.addons || {}
    return _.reduce(addons, (acc, quantity, id) => {
      const product = addonDef[id]?.product
      if (product) {
        if (_.isNil(acc)) {
          acc = []
        }
        acc.push(product.title + ' (' + quantity + ')')
      }
      return acc
    }, null)
  }


  getAddonProductDetailsForCart(inputs) {
    inputs = inputs || this.$$vm().fields || {}
    const addonDef = this.addons || {}
    const addons = inputs.addons || {}
    return _.reduce(addons, (acc, quantity, id) => {
      const product = addonDef[id]?.product
      if (product && quantity > 0) {
        acc.push({
          //@todo: Use translation
          title: 'Add-on',
          description: product.title + ' (' + quantity + ')',
        })
      }
      return acc
    }, [])
  }

  getAddonsTotal(inputs) {
    const addonDef = this.addons || {}
    const addons = inputs.addons || {}
    return _.reduce(addons, (acc, quantity, id) => {
      const product = addonDef[id]?.product
      if (product) {
        acc += _.toFinite(product.price * quantity)
      }
      return acc
    }, 0)
  }

  get rates() {
    const source = this.data.rates
    const rates = _.reduce(source, (c, i) => {
      let {tag: name = null, price = 0} = i
      price = price * 1
      name = name.toLowerCase()
      const type = c[name] || {}
      type['price'] = price
      const extras = _.omit(i, ['tag', 'price'])
      if (notEmpty(extras)) {
        _.forOwn(extras, (v, k) => {
          if (notNil(v) && price !== v * 1) {
            const key = k.replace('_price', '')
            type[key] = v * 1
          }
        })
      }
      c[name] = type
      return c
    }, {})
    return rates
  }

  get isFixedPrice() {
    return !!(1 * this.data.fixed_price)
  }

  get canDeposit() {
    return !!this.data.deposit_type
  }


  get fee() {
    return {
      percentage: this.data.fee_percentage * 1
    }
  }

  get additionalPricing() {
    const durationRaw = 1 * this.data.extra_slot_length
    const duration = this.getUnitiedDuration(durationRaw)
    const price = 1 * this.data.extra_slot_price
    const max = 1 * this.data.extra_slot_max_count
    let extra = {}
    if (max && durationRaw && price) {
      extra = {
        price,
        duration,
        durationRaw,
        max: 1 * this.data.extra_slot_max_count,
        suffix: this.getDurationText(
          duration === 1 ? 0 : durationRaw,
          this.$$t('pricing.extraHour.prefix', 'per additional'),
          this.$$t('pricing.extraHour.suffix')
        )
      }
    }
    return extra
  }

  get maxSlots() {
    return (1 * this.data.max_slots) || 1
  }

  get settings() {
    const durationRaw = this.durationRaw
    const additionalPricing = this.additionalPricing
    const now = getNow()
    let maxDateTime = null
    let minDateTime = null
    const advanceHours = this.data.hours_in_advance
    if (notNil(advanceHours)) {
      minDateTime = now.plus({hours: advanceHours})
    }
    const futureDays = this.data.days_in_future
    if (notNil(futureDays)) {
      maxDateTime = (minDateTime || now).plus({days: futureDays})
    }

    return {
      pricing: {
        perPerson: !this.isFixedPrice,
        perReservation: this.isFixedPrice,
        supportsAdditionalSlots: !empty(this.additionalPricing),
        multiRates: this.data.rates.length > 1,
        perPersonMultiRates: !this.isFixedPrice && this.data.rates.length > 1
      },
      slots: {
        max: this.maxSlots
      },

      slotsByDuration: _.reduce(_.range(1, this.maxSlots + 1), (sd, s) => {
        sd[durationRaw * s] = s
        return sd
      }, {}),

      extraSlotsByDuration: (() => {
        if (empty(additionalPricing)) {
          return {}
        }
        const baseDurationRaw = durationRaw * this.maxSlots
        const {max = 1, durationRaw: additionalDurationRaw} = additionalPricing

        return _.reduce(_.range(1, max + 1), (sd, s) => {
          sd[baseDurationRaw + additionalDurationRaw * s] = s
          return sd
        }, {})
      })(),

      participants: {
        min: 1 * this.data.min_participants,
        max: 1 * this.data.max_participants,
        maxExceed: (1 * this.data.max_participants) + 1,
        adjoining: !!(1 * this.data.adjoined_only)
      },
      buffer: {
        amount: 1 * this.data.buffer_minutes,
        type: 'minute'
      },
      advance: {
        amount: 1 * advanceHours,
        type: 'hour'
      },
      future: {
        amount: 1 * futureDays,
        type: 'day'
      },
      datetime: {
        max: maxDateTime.toISO(),
        min: minDateTime.toISO(),
      }
    }
  }

  /*  "rates": [
    {
      "tag": "kids",
      "price": "15",
      "weekend_price": ""
    },
    {
      "tag": "adults",
      "price": "5",
      "weekend_price": ""
    }
  ],
  */
  get participants() {
    const base = 'participants'
    const perPersonMultiRates = this.settings.pricing.perPersonMultiRates
    const rates = this.rates
    if (perPersonMultiRates) {
      return _.map(_.keys(rates), p => `rate_${base}_${_.snakeCase(p)}`)
    }
    return [base]
  }

  /*  "rates": [
    {
      "tag": "kids",
      "price": "15",
      "weekend_price": ""
    },
    {
      "tag": "adults",
      "price": "5",
      "weekend_price": ""
    }
  ],
  */

  get pricing() {
    const duration = this.duration
    const durationText = this.getDurationText()

    const details = this.details
    const settings = this.settings.pricing
    const {perPersonMultiRates} = settings

    const addedPrices = this.additionalPricing || {}
    let extraPricing = {}
    if (notEmpty(addedPrices)) {
      extraPricing = {extra: _.pick(addedPrices, ['price', 'suffix'])}
    }

    // {base: {price: 10, weekend: 20}}
    // {kids: {price: 10}, adults: {price: 20}}
    const rates = this.rates

    let regular = {}
    let variations = {}
    if (perPersonMultiRates) {
      _.forOwn(rates, (rate, k) => {
        const items = {base: {price: rate.price, suffix: `${k} ${durationText}`}}
        const variation = _.omit(rate, ['price'])
        if (notEmpty(variation)) {
          _.forOwn(variation, (v, vk) => {
            items[vk] = {price: v, suffix: this.getDurationText(null, '', 'for ' + vk)}
          })
        }
        variations[k] = {items: {...items, ...extraPricing}}
      })
    } else {
      _.forOwn(rates.base, (rate, k) => {
        const titlePath = `pricing.rates.${k}.title`
        const defaultTitle = (k === 'price' ? 'regular' : k) + ' price'
        const title = _.get(details, titlePath, this.$$t(titlePath, defaultTitle))
        const rateDetails = {
          title,
          items: {
            base: {price: rate, suffix: `${durationText}`},
            ...extraPricing
          }
        }
        // set to base category if property is 'price'
        if (k === 'price') {
          regular = rateDetails
        } else {
          variations[k] = rateDetails
        }

      })
    }

    const fee = this.fee
    let additional = {}
    let additionalItems = {}
    if (fee.percentage) {
      additionalItems.fee = {label: fee.percentage + '%', suffix: this.$$t('pricing.feePercentage.suffix', '')}
    }

    const upgrades = this.upgrades
    if (notEmpty(upgrades)) {
      _.forOwn(upgrades, (u, uk) => {
        additionalItems[`upgrade_${uk}`] = {
          price: u.price,
          prefix: this.$$t(`pricing.upgrades.${uk}.prefix`),
          suffix: this.$$t(`pricing.upgrades.${uk}.suffix`, u.title)
        }
      })
    }
    if (notEmpty(additionalItems)) {
      const titlePath = `pricing.additional.title`
      const defaultTitle = 'Additional items'
      const title = this.$$t(titlePath, _.get(details, titlePath, defaultTitle))
      additional = {title, items: additionalItems}
    }

    let type = _.size(rates) === 1 && _.size(_.first(_.values(rates))) === 1 ? 'fixed' : 'multi' // range
    const categories = {regular, ...variations, additional} // base/...variations/additional
    const pricing = this.mergeComboPricing({type, categories})
    return pricing

  }

  getDurationOption(rawValue) {
    const unit = this.durationUnit
    const value = this.getUnitiedDuration(rawValue)
    return this.$$tc(`duration.options.${unit}`, value + ' ' + unit, value)
  }

  fillComboProducts() {
    const id = 1 * this.details.combo_id
    if (id) {
      const all = _.get(window.addiesaas || {}, `products.types.${PRODUCT_TYPE}`, {})
      _.forOwn(all, p => {
        if ((p.custom.combo_id * 1) === id && (p.id * 1) !== (this.id * 1)) {
          this.comboProducts[p.id] = new Reservation(p, this.$$vm())
        }
      })
    }
  }

  detectComboProduct(factorValue) {
    let product = this

    if (notEmpty(this.combos)) {
      const switcher = this.details.combo_switch_field
      if (switcher) {
        if (_.isObject(factorValue) && _.has(factorValue, switcher)){
          factorValue = _.get(factorValue, switcher)
        }

        const operatorName = this.details.combo_switch_operator
        const operator = formatters.getFirst(operatorName, formatters, this.$$vm())

        if (operator) {
          const selectProduct = _.findLast(this.combos, c => {
            const switchValue = c.data[switcher]
            return operator.call(this.$$vm(), factorValue, switchValue)
          })

          if (selectProduct) {
            product = selectProduct
          }
        }
      }
    }
    return product
  }

  detectComboProductId(factorValue) {
    const product = this.detectComboProduct(factorValue)

    return product.id
  }

  durationText() {
    return this.getDurationText()
  }

  getUnitiedDuration(duration) {
    return _.multiply(nil(duration) ? this.durationRaw : duration, this.durationFactor)
  }

  getDurationText(duration, prefix = '', suffix = '') {
    const unit = this.durationUnit
    const value = duration === 0 ? 0 : this.getUnitiedDuration(duration)
    return (nil(prefix) ? '' : (prefix + ' ')) +
      this.$$tc(`duration.${unit}`, 'per ' + unit, value) +
      (nil(suffix) ? '' : (' ' + suffix))
  }

  mergeComboPricing(pricing) {
    const comboRates = _.mapValues(this.combos || {}, c => c.pricing)
    if (empty(comboRates)) {
      return pricing
    }
    _.forOwn(comboRates, (c, cid) => {
      const mergedCategories = _.merge({}, pricing.categories, c.categories)
      _.forOwn(mergedCategories, (mc, mck) => {
        if (mc.items.base) {
          mergedCategories[mck].items = {
            base: pricing.categories[mck].items.base,
            [`base_${cid}`]: c.categories[mck].items.base,
            ..._.omit(mc.items, ['base'])
          }
        }
      })
      pricing.categories = mergedCategories
    })
    return pricing
  }

  sanitizePayload(payload) {
    if (_.has(payload, 'participants')) {
      if (_.isString(payload.participants)) {
        payload.participants = 1 * payload.participants
      }
    }
    if (_.isObject(payload.addons) && !_.isEmpty(payload.addons)) {
        payload.addons = _.pickBy(payload.addons) // removes all zero quantity items
    }
    return payload
  }

  async getUnavailability(inputs) {
    const fields = _.cloneDeep(inputs)
    const expectedFilled = _.pick(fields, ['date', 'slots']);
    this.validateServiceCallArguments(expectedFilled)
    const payload = _.pick(fields, ['date', 'product_id', 'participants', 'slots', 'extra_slots', 'ignore_reservation_hold_id'])
    return await this.serviceCall('products.reservation.unavailability', this.sanitizePayload(payload))
  }

  async checkAvailability(inputs) {
    const fields = _.cloneDeep(inputs)
    const expectedFilled = _.pick(fields, ['date', 'time', 'participants', 'slots']);
    this.validateServiceCallArguments(expectedFilled)
    const payload = _.pick(fields, ['start_at', 'product_id', 'participants', 'slots', 'extra_slots', 'ignore_reservation_hold_id'])
    return await this.serviceCall('products.reservation.availability', this.sanitizePayload(payload))
  }

  async hold(inputs) {
    const fields = _.cloneDeep(inputs)
    const payload = _.pick(fields, ['start_at', 'product_id', 'participants', 'slots', 'extra_slots', 'ignore_reservation_hold_id'])
    return await this.serviceCall('products.reservation.hold', this.sanitizePayload(payload))
  }

  async validate(inputs) {
    const cartItem = this.getCartItem({...inputs, deposit: true})
    const order = {items: [cartItem.payload ]}
    const data = await this.serviceCall('user.validate.order', order)
    const costDetails = data.data
    return costDetails
  }

  getCartItemStub(inputs) {
    const fields = _.cloneDeep(inputs)
    const product = this.detectComboProduct(fields)
    const productData = _.cloneDeep(product.data)

    const payload = {
      product_id: product.id,
      options: {
        ...this.sanitizePayload(
          _.pick(
            fields,
            ['reservation_hold_id', 'start_at', 'participants', 'notes', 'slots', 'extra_slots', 'addons']
          )
        ),
        contact: _.pick(fields, ['first_name', 'last_name', 'email', 'phone']),
        ...groupByKeyPrefix(fields, 'guest_of_honor'),
        ...groupByKeyPrefix(fields, 'upgrades', '_', toNumber, true),
        ...groupByKeyPrefix(fields, 'rate_participants', '_', toNumber, true),
        //assuming that there will be only one rate in fixedPrice scenario
        ...notNils({rate: product.settings.perPersonMultiRates ? null : firstKey(this.rates)})
      },
      deposit: formatters.fv(fields.deposit, true),
    }

    // Adjust product price for cart display
    productData.price = this.getBasePrice(inputs)

    return {
      payload,
      total: _.get(fields, 'costDetails.price'),
      title: product.title,
      type: product.type,
      editable: true,
      //editRoute:
      deletable: true,
      // hideQuantity: true,
      cartCanChangeQuantity: false,
      attributes: {
        fields,
        product: productData,
        info: [
          [
            {
              title: 'Date',
              description: fields.date,
            },
            {
              title: 'Time',
              description: fields.time
            },
            {
              title: 'Participants',
              description: fields.participants
            },
            ...this.getAddonProductDetailsForCart(inputs)
          ]
        ],
      }
    }
  }

  getCartItem(inputs) {
    const item = this.getCartItemStub(inputs)
    if (nil(item.total)) {
      item.total = this.getEstimatedTotal(inputs)
    }
    return item
  }

  getBasePrice(inputs, vm) {
    const fields = _.cloneDeep(inputs)
    const product = this.detectComboProduct(fields)
    const date = parseDateTime(fields.start_at)
    const priceKey = this.isWeekend(date) ? 'weekend': 'price'

    const priceSettings = product.settings.pricing
    const {perPerson, multiRates} = priceSettings
    const quantity = (perPerson && !multiRates ? 1 * fields.participants : 1) || 1
    const productRates = product.rates
    const firstRate = first(productRates)
    const price = quantity * this.$$fs(firstRate[priceKey], firstRate['price'])

    return price
  }

  isWeekend(startAt) {
    startAt = parseDateTime(startAt)
    if (window.addiesaas?.location?.weekend) {
      const weekend = [...window.addiesaas.location.weekend]

      for (let i of [0, 1]) {
        weekend[i] = weekend[i].split(' ', 2)
        weekend[i][0] = parseInt(weekend[i][0]) // Day of week
        weekend[i][1] = weekend[i][1].split(':', 2)
        weekend[i][1] = parseInt(weekend[i][1][0]) * 60 + parseInt(weekend[i][1][1]) // Minute of day
        weekend[i] = weekend[i][0] * 24 * 60 + weekend[i][1]; // Minute of week
      }

      let [weekendStart, weekendEnd] = weekend

      let minuteOfWeek = ((startAt.weekday % 7) * 24 + startAt.hour) * 60 + startAt.minute

      if (weekendStart > weekendEnd) {
        weekendEnd += 7 * 24 * 60
        if (minuteOfWeek < weekendStart) {
          minuteOfWeek += 7 * 24 * 60
        }
      }

      if (minuteOfWeek >= weekendStart && minuteOfWeek <= weekendEnd) {
        return true
      }

      return false
    }

    return startAt.weekday > 5
  }

  getMultiBasePrice(inputs) {
    const details = this.details
    const fields = _.cloneDeep(inputs)
    const product = this.detectComboProduct(fields)
    const date = parseDateTime(fields.start_at)
    const priceKey = this.isWeekend(date) ? 'weekend': 'price'

    const prices = {}
    const productRates = product.rates
    _.forOwn(productRates, (rate, key) => {
      const price = this.$$fs(rate[priceKey], rate['price'])
      const quantity = 1 * fields[`rate_participants_${key}`]

      const titlePath = `pricing.rate.${key}.title`
      const defaultTitle = key === 'base' ? '' : _.startCase(key)
      const title = _.get(details, titlePath, this.$$t(titlePath, defaultTitle))
      if (quantity) {
        prices[key] = {
          title,
          value: [quantity, '/', formatters.currency(price * (quantity || 1))].join('')
        }
      }
    })
    if (empty(prices)) {
      return null
    }
    return prices
  }

  getEstimatedTotal(inputs, vm) {

    const fields = _.cloneDeep(inputs)
    const product = this.detectComboProduct(fields)
    const cartItem = this.getCartItemStub(fields)
    const details = cartItem.payload.options
    const participants = 1 * details.participants || 0
    const date = parseDateTime(details.start_at)
    const priceKey = this.isWeekend(date) ? 'weekend': 'price'

    const isPerReservation = product.settings.pricing.perReservation
    const productRates = product.rates
    // assuming that there will be only one rate
    const cartRates = details.rate_participants || {[details.rate]: participants}
    const slots = details.slots || 1

    let value = _.reduce(cartRates, (total, count, key) => {
      const price = this.$$fs(productRates[key][priceKey], productRates[key]['price'])
      total += 1 * (slots * price * (isPerReservation ? 1 : count)) || 0
      return total
    }, 0)

    const productExtraRates = product.additionalPricing
    const extraSlots = details.extra_slots || 0
    if (extraSlots) {
      value += 1 * (extraSlots * productExtraRates.price * (isPerReservation ? 1 : participants)) || 0
    }

    const upgrades = details.upgrades
    if (notEmpty(upgrades)) {
      const productUpgrades = product.upgrades
      _.forOwn(upgrades, (count, upgradeTag) => {
        const upgrade = productUpgrades[upgradeTag]
        if (upgrade) {
          value += 1 * (count * upgrade.price * (upgrade.perReservation ? 1 : participants)) || 0
        }
      })
    }

    const productFee = product.fee

    const addonsTotal = this.getAddonsTotal(inputs)

    return value + addonsTotal
  }

  getAdditionalDurationPrice(inputs) {
    const options = this.additionalDurationOptions
    const durations = this.slotsByDuration
    const fields = _.cloneDeep(inputs)
    const perPerson = this.settings.pricing.perPerson
    const participants = perPerson ? (1 * fields.participants || 1) : 1
    const duration = 1 * fields.duration


    const hasAdditional = this.settings.pricing.supportsAdditionalSlots
    if (!hasAdditional) {
      return null
    }
    if (!options[duration]) {
      return null
    }
    const additional = [options[duration]]

    const product = this.detectComboProduct(fields)
    const additionalPricing = product.additionalPricing
    const extras = this.extraSlotsByDuration

    if (extras[duration]) {
      const price = additionalPricing.price * participants * extras[duration]
      if (price) {
        additional.push(formatters.currency(price))
      }
    }
    return additional.join('/')
  }
}

Product.RESERVATION = Reservation
export default Reservation
